import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static values = {
    type: String,
    data: Object
  }

  connect() {
    this.renderChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  get isDark() {
    return document.documentElement.classList.contains("dark")
  }

  get textColor() {
    return this.isDark ? "rgba(203, 213, 225, 0.9)" : undefined
  }

  get gridColor() {
    return this.isDark ? "rgba(100, 116, 139, 0.3)" : undefined
  }

  renderChart() {
    const ctx = this.element.getContext?.("2d")
    if (!ctx) return

    const data = this.dataValue
    const isDark = this.isDark

    let chartData
    let chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: this.typeValue === "doughnut",
          labels: { color: this.textColor }
        }
      }
    }

    if (this.typeValue === "line") {
      // Line chart for calls over time — keys are ISO date strings
      const labels = Object.keys(data).sort()
      const values = labels.map(key => data[key])

      chartData = {
        labels: labels,
        datasets: [{
          label: "Calls",
          data: values,
          borderColor: "rgb(59, 130, 246)",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          tension: 0.3
        }]
      }
      if (isDark) {
        chartOptions.scales = {
          x: { ticks: { color: this.textColor }, grid: { color: this.gridColor } },
          y: { ticks: { color: this.textColor }, grid: { color: this.gridColor } }
        }
      }
    } else if (this.typeValue === "bar") {
      // Bar chart for calls by hour
      const hours = Array.from({ length: 24 }, (_, i) => i)
      const values = hours.map(hour => data[hour] || 0)

      chartData = {
        labels: hours.map(h => `${h}:00`),
        datasets: [{
          label: "Calls",
          data: values,
          backgroundColor: "rgba(59, 130, 246, 0.6)"
        }]
      }

      chartOptions.scales = {
        x: {
          ticks: { maxTicksLimit: 12, color: this.textColor },
          grid: { color: this.gridColor }
        },
        y: {
          ticks: { color: this.textColor },
          grid: { color: this.gridColor }
        }
      }
    } else if (this.typeValue === "doughnut") {
      // Doughnut chart for stage distribution
      const labels = Object.keys(data)
      const values = Object.values(data)

      chartData = {
        labels: labels.map(l => l.charAt(0).toUpperCase() + l.slice(1)),
        datasets: [{
          data: values,
          backgroundColor: [
            "rgba(156, 163, 175, 0.8)",
            "rgba(59, 130, 246, 0.8)",
            "rgba(245, 158, 11, 0.8)",
            "rgba(16, 185, 129, 0.8)"
          ]
        }]
      }

      // Doughnut keeps aspect ratio since it's not in a height-constrained container
      chartOptions.maintainAspectRatio = true
    } else if (this.typeValue === "funnel") {
      // Horizontal stacked bar for sales funnel
      const stages = data.stages || []
      chartData = {
        labels: [""],
        datasets: stages.map(stage => ({
          label: `${stage.label} (${stage.count})`,
          data: [stage.count],
          backgroundColor: stage.color,
          barThickness: 40
        }))
      }

      chartOptions = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { display: false }
          },
          y: {
            stacked: true,
            grid: { display: false },
            ticks: { display: false }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: function(context) {
                const stage = stages[context.datasetIndex]
                if (!stage) return ""
                return `${stage.label}: ${stage.count} (${stage.rate}%)`
              }
            }
          }
        }
      }
    } else if (this.typeValue === "horizontal_bar") {
      // Horizontal bar for stage duration averages
      const labels = Object.keys(data)
      const values = Object.values(data).map(v => v.value)
      const colors = Object.values(data).map(v => v.color)

      chartData = {
        labels: labels,
        datasets: [{
          label: "Avg Duration",
          data: values,
          backgroundColor: colors,
          barThickness: 30
        }]
      }

      const formatDuration = function(seconds) {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        if (mins > 0 && secs > 0) return `${mins}m ${secs}s`
        if (mins > 0) return `${mins}m`
        return `${secs}s`
      }

      chartOptions = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: this.textColor,
              callback: function(value) {
                return formatDuration(value)
              }
            }
          },
          y: {
            grid: { display: false },
            ticks: { color: this.textColor }
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: function(context) {
                return formatDuration(context.raw)
              }
            }
          }
        }
      }
    }

    if (!chartData) {
      console.warn(`analytics-chart: unknown chart type "${this.typeValue}"`)
      return
    }

    this.chart = new Chart(ctx, {
      type: this.typeValue === "funnel" || this.typeValue === "horizontal_bar" ? "bar" : this.typeValue,
      data: chartData,
      options: chartOptions
    })
  }
}
